import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

export interface PolicySection {
  id: string;
  title: string;
  description: string;
  points: string[];
}

export interface Story {
  id: number;
  name: string;
  category: string;
  title: string;
  content: string;
  date: Date;
}

@Component({
  selector: 'app-page-privacy',
  templateUrl: './page-privacy.component.html',
  styleUrls: ['./page-privacy.component.css'],
})
export class PagePrivacyComponent implements OnInit {

  // Set Topbar Option
  Menuoption = 'center';
  Settingicon = true;

  // Company Info
  companyName = 'BLMPI Tourism';
  lastRevised = '23rd Sep, 2021';

  // Policy sections tied to BLMPI services
  policySections: PolicySection[] = [
    {
      id: 'adventures',
      title: 'Adventures',
      description:
        'When you book adventure trips with BLMPI, we collect only the essential information needed to keep you safe and personalize your experience.',
      points: [
        'Name, age and (when required) health condition for safety.',
        'Emergency contact shared only with local certified guides.',
        'Your data is never sold or shared with third-party advertisers.',
      ],
    },
    {
      id: 'camping',
      title: 'Camping',
      description:
        'Camping data is used solely to confirm your booking, organize groups and assign campsites efficiently.',
      points: [
        'Campsite location and stay duration are stored for logistics.',
        'You may request deletion of your camping data after the trip.',
        'Photos taken during camping are yours — we ask permission before sharing.',
      ],
    },
    {
      id: 'foods',
      title: 'Foods',
      description:
        'We keep your dietary preferences and allergies on file to guarantee a safe and enjoyable culinary journey.',
      points: [
        'Food preferences are used only during trip planning.',
        'Health-related info is never shared with restaurants without consent.',
        'You can update or remove your preferences anytime.',
      ],
    },
    {
      id: 'souvenirs',
      title: 'Souvenirs',
      description:
        'Purchasing souvenirs through BLMPI is protected by industry-standard encryption and secure payment gateways.',
      points: [
        'All payments are processed through PCI-DSS compliant gateways.',
        'We do not store credit card data on our servers.',
        'Order history is kept only for warranty and support purposes.',
      ],
    },
    {
      id: 'visas',
      title: 'Visas',
      description:
        'Visa services require sensitive documents — we handle them under the strictest confidentiality standards.',
      points: [
        'Documents are encrypted at rest and in transit.',
        'Access is restricted to BLMPI visa officers only.',
        'All documents are automatically purged after processing.',
      ],
    },
  ];
  // Account Deletion info
  deletionSteps: string[] = [
    'Open the BLMPI mobile app on your device (Android or iOS).',
    'Sign in to your account if you are not already logged in.',
    'Navigate to your Profile page from the bottom navigation bar.',
    'Scroll down and tap the "Delete Account" button.',
    'Confirm the deletion when prompted — this action is permanent.',
  ];

  deletionInfo = {
    title: 'Account Deletion',
    intro:
      'You have the right to delete your BLMPI account at any time, directly from the mobile application on both Android and iOS devices. No email request or phone call is required.',
    warning:
      'Warning: Deleting your account is permanent and cannot be undone. All your personal data, bookings, stories and saved preferences will be removed from our systems.',
    dataDeleted: [
      'Your profile information (name, email, phone number).',
      'All your uploaded stories and photos.',
      'Your booking history for Adventures, Camping, Foods, Souvenirs and Visas.',
      'Saved payment methods and preferences.',
      'Any loyalty points or promotional credits.',
    ],
    dataRetained: [
      'Financial records required by law (kept for up to 5 years).',
      'Anonymized analytics data with no link to your identity.',
    ],
    processingTime: 'Your account and associated data will be permanently deleted within 30 days of your request.',
    supportEmail: 'privacy@blmpi.com',
  };
    // Story Deletion info
  storyDeletionInfo = {
    title: 'Story Deletion',
    intro:
      'You can delete any story you have shared on BLMPI directly from the mobile application on both Android and iOS devices. No email request is required.',
    steps: [
      'Open the BLMPI mobile app on your device (Android or iOS).',
      'Sign in and go to the Stories section.',
      'Long press on the story you want to delete.',
      'A quick menu will appear with a "Delete Story" option.',
      'Tap "Delete Story" to permanently remove it.',
    ],
    warning:
      'Deleting a story is permanent and cannot be undone. The story, its text and any attached photos will be removed from our servers and will no longer be visible to other travelers.',
    note:
      'The "Delete Story" option is available only on your own stories, and only through the BLMPI mobile app (Android & iOS).',
    supportEmail: 'privacy@blmpi.com',
  };
  // Story Form
  storyForm!: FormGroup;
  stories: Story[] = [];
  submitted = false;
  categories = ['Adventures', 'Camping', 'Foods', 'Souvenirs', 'Visas'];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.storyForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      category: ['', Validators.required],
      title: ['', [Validators.required, Validators.minLength(4)]],
      content: ['', [Validators.required, Validators.minLength(20)]],
    });

    // Load saved stories from localStorage
    const saved = localStorage.getItem('blmpi_stories');
    if (saved) {
      this.stories = JSON.parse(saved);
    }
  }

  get f() {
    return this.storyForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.storyForm.invalid) return;

    const newStory: Story = {
      id: Date.now(),
      ...this.storyForm.value,
      date: new Date(),
    };

    this.stories.unshift(newStory);
    this.persistStories();

    this.storyForm.reset();
    this.submitted = false;
  }

  deleteStory(id: number): void {
    this.stories = this.stories.filter((s) => s.id !== id);
    this.persistStories();
  }

  private persistStories(): void {
    localStorage.setItem('blmpi_stories', JSON.stringify(this.stories));
  }

  trackById(_: number, item: Story): number {
    return item.id;
  }
}